import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  ServiceResponse,
  ServicesListResponse,
  IServiceData,
} from '@/types/service-management.types';

export interface HarborServiceQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  includeArchived?: boolean;
  lang?: string;
  'x-session-id'?: string;
  'x-location-name'?: string;
  'x-latitude'?: number;
  'x-longitude'?: number;
}

export const HarborServiceManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all services owned by a harbor
    getHarborServices: builder.query<
      PaginatedApiResponse<IServiceData[]>,
      { harborId: string; params?: HarborServiceQueryParams }
    >({
      query: ({ harborId, params }) => ({
        url: `/harbors/${harborId}/harbor-services`,
        method: 'GET',
        params,
        headers: {
          'x-lang': params?.lang || 'en',
          ...(params?.['x-session-id'] && {
            'x-session-id': params['x-session-id'],
          }),
          ...(params?.['x-location-name'] && {
            'x-location-name': params['x-location-name'],
          }),
          ...(params?.['x-latitude'] && {
            'x-latitude': params['x-latitude'].toString(),
          }),
          ...(params?.['x-longitude'] && {
            'x-longitude': params['x-longitude'].toString(),
          }),
        },
      }),
      transformResponse: (response: ServicesListResponse) => ({
        data: response.data,
        totalItems: response.total,
        totalPages: response.totalPages,
        currentPage: response.currentPage,
        status: response.status,
        message: response.message,
      }),
      providesTags: (result, error, { harborId }) => [
        { type: 'HarborServices', id: harborId },
      ],
      keepUnusedDataFor: 0,
    }),

    // Get a single harbor service by ID
    getHarborServiceById: builder.query<
      ServiceResponse,
      {
        harborId: string;
        serviceId: string;
        params?: {
          lang?: string;
          'x-session-id'?: string;
          'x-location-name'?: string;
          'x-latitude'?: number;
          'x-longitude'?: number;
        };
      }
    >({
      query: ({ harborId, serviceId, params }) => ({
        url: `/harbors/${harborId}/harbor-services/${serviceId}`,
        method: 'GET',
        headers: {
          'x-lang': params?.lang || 'en',
          ...(params?.['x-session-id'] && {
            'x-session-id': params['x-session-id'],
          }),
          ...(params?.['x-location-name'] && {
            'x-location-name': params['x-location-name'],
          }),
          ...(params?.['x-latitude'] && {
            'x-latitude': params['x-latitude'].toString(),
          }),
          ...(params?.['x-longitude'] && {
            'x-longitude': params['x-longitude'].toString(),
          }),
        },
      }),
      transformResponse: (response: ServiceResponse) => response,
      providesTags: (result, error, { harborId, serviceId }) => [
        { type: 'HarborServices', id: `${harborId}-${serviceId}` },
      ],
      keepUnusedDataFor: 0,
    }),

    // Create a new harbor service
    createHarborService: builder.mutation<
      ApiResponse<IServiceData>,
      {
        harborId: string;
        data: FormData;
        params?: {
          lang?: string;
          'x-session-id'?: string;
          'x-location-name'?: string;
          'x-latitude'?: number;
          'x-longitude'?: number;
        };
      }
    >({
      query: ({ harborId, data, params }) => ({
        url: `/harbors/${harborId}/harbor-services`,
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-lang': params?.lang || 'en',
          ...(params?.['x-session-id'] && {
            'x-session-id': params['x-session-id'],
          }),
          ...(params?.['x-location-name'] && {
            'x-location-name': params['x-location-name'],
          }),
          ...(params?.['x-latitude'] && {
            'x-latitude': params['x-latitude'].toString(),
          }),
          ...(params?.['x-longitude'] && {
            'x-longitude': params['x-longitude'].toString(),
          }),
        },
      }),
      transformResponse: (response: ApiResponse<IServiceData>) => response,
      invalidatesTags: (result, error, { harborId }) => [
        { type: 'HarborServices', id: harborId },
      ],
    }),

    // Update a harbor service
    updateHarborService: builder.mutation<
      ApiResponse<IServiceData>,
      {
        harborId: string;
        serviceId: string;
        data: FormData;
        params?: {
          lang?: string;
          'x-session-id'?: string;
          'x-location-name'?: string;
          'x-latitude'?: number;
          'x-longitude'?: number;
        };
      }
    >({
      query: ({ harborId, serviceId, data, params }) => ({
        url: `/harbors/${harborId}/harbor-services/${serviceId}`,
        method: 'PATCH',
        data,
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-lang': params?.lang || 'en',
          ...(params?.['x-session-id'] && {
            'x-session-id': params['x-session-id'],
          }),
          ...(params?.['x-location-name'] && {
            'x-location-name': params['x-location-name'],
          }),
          ...(params?.['x-latitude'] && {
            'x-latitude': params['x-latitude'].toString(),
          }),
          ...(params?.['x-longitude'] && {
            'x-longitude': params['x-longitude'].toString(),
          }),
        },
      }),
      transformResponse: (response: ApiResponse<IServiceData>) => response,
      invalidatesTags: (result, error, { harborId, serviceId }) => [
        { type: 'HarborServices', id: harborId },
        { type: 'HarborServices', id: `${harborId}-${serviceId}` },
      ],
    }),

    // Toggle harbor service visibility
    toggleHarborServiceVisibility: builder.mutation<
      ApiResponse<IServiceData>,
      {
        harborId: string;
        serviceId: string;
        is_visible: boolean;
        params?: {
          lang?: string;
          'x-session-id'?: string;
          'x-location-name'?: string;
          'x-latitude'?: number;
          'x-longitude'?: number;
        };
      }
    >({
      query: ({ harborId, serviceId, is_visible, params }) => ({
        url: `/harbors/${harborId}/harbor-services/${serviceId}/visibility`,
        method: 'PATCH',
        data: { is_visible: is_visible },
        headers: {
          'Content-Type': 'application/json',
          'x-lang': params?.lang || 'en',
          ...(params?.['x-session-id'] && {
            'x-session-id': params['x-session-id'],
          }),
          ...(params?.['x-location-name'] && {
            'x-location-name': params['x-location-name'],
          }),
          ...(params?.['x-latitude'] && {
            'x-latitude': params['x-latitude'].toString(),
          }),
          ...(params?.['x-longitude'] && {
            'x-longitude': params['x-longitude'].toString(),
          }),
        },
      }),
      transformResponse: (response: ApiResponse<IServiceData>) => response,
      invalidatesTags: (result, error, { harborId, serviceId }) => [
        { type: 'HarborServices', id: harborId },
        { type: 'HarborServices', id: `${harborId}-${serviceId}` },
      ],
    }),

    // Delete (archive) a harbor service
    deleteHarborService: builder.mutation<
      ApiResponse<void>,
      {
        harborId: string;
        serviceId: string;
        params?: {
          lang?: string;
          'x-session-id'?: string;
          'x-location-name'?: string;
          'x-latitude'?: number;
          'x-longitude'?: number;
        };
      }
    >({
      query: ({ harborId, serviceId, params }) => ({
        url: `/harbors/${harborId}/harbor-services/${serviceId}`,
        method: 'DELETE',
        headers: {
          'x-lang': params?.lang || 'en',
          ...(params?.['x-session-id'] && {
            'x-session-id': params['x-session-id'],
          }),
          ...(params?.['x-location-name'] && {
            'x-location-name': params['x-location-name'],
          }),
          ...(params?.['x-latitude'] && {
            'x-latitude': params['x-latitude'].toString(),
          }),
          ...(params?.['x-longitude'] && {
            'x-longitude': params['x-longitude'].toString(),
          }),
        },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: (result, error, { harborId, serviceId }) => [
        { type: 'HarborServices', id: harborId },
        { type: 'HarborServices', id: `${harborId}-${serviceId}` },
      ],
    }),

    downloadHarborServiceQrCode: builder.mutation<
      Blob,
      { harborId: string; serviceId: string }
    >({
      query: ({ harborId, serviceId }) => ({
        url: `harbors/${harborId}/harbor-services/${serviceId}/qr-code`,
        method: 'GET',
        responseType: 'blob',
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
    }),
  }),
});

export const {
  useGetHarborServicesQuery,
  useGetHarborServiceByIdQuery,
  useCreateHarborServiceMutation,
  useUpdateHarborServiceMutation,
  useDeleteHarborServiceMutation,
  useToggleHarborServiceVisibilityMutation,
  useDownloadHarborServiceQrCodeMutation,
} = HarborServiceManagementService;
